import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';

import { Subject } from 'rxjs';
import { debounceTime, finalize, switchMap, tap } from 'rxjs/operators';

import {
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';

@Component({
	selector: 'app-account-search',
	templateUrl: './account-search.component.html',
	styleUrls: ['./account-search.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class AccountSearchComponent implements OnInit {
	@Input() accountSelected: any = null;
	@Input() placeholder: string = 'Broker';
	@Input() customFormFieldClass: string = 'custom-form-field';
	@Input() showCloseIcon: boolean = false;
	@Input() usePopover: boolean = true;
	@Input() useAutoComplete: boolean = false;
	@Input() useDropdown: boolean = false;

	@Output() setAccount = new EventEmitter<any>();

	accountsList: Array<any> = [];
	isLoadingAccountsList: boolean = false;
	searchText: string = '';
	dropdownPage: number = 0;
	dropdownSize: number = 50;
	hasNextPage: boolean = false;

	debouncer: Subject<void> = new Subject<void>();
	@ViewChild('accountInput') accountInput: ElementRef;
	@ViewChild('auto') matAutocomplete: MatAutocomplete;

	@ViewChild(MtxPopoverTrigger) trigger: MtxPopoverTrigger;

	constructor(
		private urlService: UrlService,
		private restService: RestService,
		public utilService: UtilService
	) {}

	ngOnInit(): void {
		if (this.useAutoComplete) {
			this.debouncer
				.pipe(
					debounceTime(300),
					tap(() => {
						this.isLoadingAccountsList = true;
						this.accountsList = [];
					}),
					switchMap(() => {
						const url = this.urlService.searchBrokers(this.searchText, 0, 5);
						return this.restService.GET(url);
					})
				)
				.subscribe((response: any) => {
					this.accountsList = response['content'].sort(
						(a, b) => 0 - (a.businessName.toLowerCase() > b.businessName.toLowerCase() ? -1 : 1)
					);
					this.isLoadingAccountsList = false;
				});
		} else {
			this.getAccountsList();
		}
	}

	ngOnChanges() {
		if (IS_NULL_OR_UNDEFINED(this.accountSelected)) {
			this.searchText = '';
		} else {
			if (this.useAutoComplete) {
				this.searchText = this.accountSelected;
			} else {
				this.setAccountSelected(this.accountSelected);
			}
		}
	}

	inputChanged(searchedText: string) {
		if (IS_NOT_EMPTY(searchedText)) {
			this.debouncer.next();
		} else {
			this.setAccount.emit(null);
			this.isLoadingAccountsList = false;
		}
	}

	selected(event: MatAutocompleteSelectedEvent): void {
		this.setAccount.emit(event.option.value);
	}

	displayFn(carrier: any) {
		return carrier && carrier.businessName ? carrier.businessName : '';
	}

	renderDownArrowIcon(): boolean {
		if (!this.showCloseIcon || (this.showCloseIcon && IS_NULL_OR_UNDEFINED(this.accountSelected))) {
			return true;
		}
		return false;
	}

	renderCloseArrowIcon(): boolean {
		if (this.showCloseIcon && IS_NOT_NULL_OR_UNDEFINED(this.accountSelected)) {
			return true;
		}
		return false;
	}

	removeSelectedAccount() {
		this.accountSelected = null;
		this.setAccount.emit(null);
		this.placeholder = 'Broker';
	}

	getAccountsList() {
		this.isLoadingAccountsList = true;
		const url = this.urlService.searchBrokers('', this.dropdownPage, this.dropdownSize);
		this.restService
			.GET(url)
			.pipe(
				finalize(() => {
					this.isLoadingAccountsList = false;
				})
			)
			.subscribe(
				(response) => {
					this.accountsList = response['content'].sort(
						(a, b) => 0 - (a.businessName.toLowerCase() > b.businessName.toLowerCase() ? -1 : 1)
					);
					if (response.totalPages > this.dropdownPage + 1) {
						this.hasNextPage = true;
					} else {
						this.hasNextPage = false;
					}
				},
				(error) => {
					this.utilService.showSnackbar(
						`Unable to fetch broker list`,
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
				}
			);
	}

	setAccountSelected(account: any) {
		this.accountSelected = account;
		this.placeholder = `${this.accountSelected?.businessName}`;
		this.setAccount.emit(this.accountSelected);
		if (IS_NOT_NULL_OR_UNDEFINED(this.trigger)) {
			this.trigger.closePopover();
		}
	}

	viewMore() {
		this.dropdownPage += 1;
		this.getAccountsList();
	}
}
